import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Users, X } from "lucide-react";
import type { StaticCountsBo } from "@/types/api/api-types";
import { useGetUsersBo } from "@/api/user.api";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { DraftMode } from "./expeditionsBoxFilters";

type AssigneeBadgeProps = {
  label: string;
  count?: number;
  isSelected: boolean;
  isMe?: boolean;
  className?: string;
  onClick: () => void;
};

const AssigneeBadge = ({
  label,
  count,
  isSelected,
  isMe,
  className,
  onClick,
}: AssigneeBadgeProps) => {
  if (!count) return null;

  return (
    <button
      onClick={onClick}
      className={cn(
        "flex h-[22px] cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-xs transition-colors",
        isMe ? "font-medium" : "font-semibold uppercase",
        isSelected
          ? "border border-[#3562f8] bg-[rgba(53,98,248,0.05)] text-[#3562f8]"
          : isMe
            ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
            : "bg-gray-200 text-slate-500 hover:bg-gray-300",
        className,
      )}
    >
      <span>{label}</span>
      <span className={cn("font-medium", isSelected ? "opacity-80" : "opacity-60")}>{count}</span>
    </button>
  );
};

type DraftCounts = Pick<StaticCountsBo, "drafts" | "myDrafts" | "lateDrafts">;

type AssigneeFilterProps = {
  selectedAssigneeId: string | null;
  onSelect: (id: string | null) => void;
  byAssignee?: Record<string, number | undefined>;
  restrictToUserId?: string;
  myUserId: string;
  draftMode: DraftMode;
  draftCounts?: DraftCounts;
  onDraftModeChange: (mode: DraftMode) => void;
};

export const AssigneeFilter = ({
  selectedAssigneeId,
  onSelect,
  byAssignee,
  restrictToUserId,
  myUserId,
  draftMode,
  draftCounts,
  onDraftModeChange,
}: AssigneeFilterProps) => {
  const { t } = useTranslation("boExpeditions");
  const { data: users } = useGetUsersBo({ isDeleted: false });

  const handleSelect = (id: string) => {
    onSelect(selectedAssigneeId === id ? null : id);
  };

  const visibleUsers = (restrictToUserId ? users?.filter((u) => u.id === restrictToUserId) : users)
    ?.slice()
    .sort((a, b) => (a.id === myUserId ? -1 : b.id === myUserId ? 1 : 0));

  return (
    <div className="flex flex-wrap items-center justify-end gap-4 rounded-xl border-1 bg-white px-4 py-2">
      <DraftFilterButton draftMode={draftMode} counts={draftCounts} onSelect={onDraftModeChange} />
      <span className="hidden h-[40px] w-[2px] bg-gray-300 sm:block" />
      <div className="flex items-center gap-2">
        <Users className="size-4 text-gray-500" />
        <span className="font-jakarta-sans text-sm font-semibold tracking-wide text-gray-900 uppercase">
          {t("assigneeFilter.title")}
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {visibleUsers?.map((user) => {
          const isMe = user.id === myUserId;
          return (
            <AssigneeBadge
              key={user.id}
              label={isMe ? t("assigneeFilter.myAssignments") : user.initials}
              count={byAssignee?.[user.id]}
              isSelected={selectedAssigneeId === user.id}
              isMe={isMe}
              onClick={() => handleSelect(user.id)}
            />
          );
        })}
        {!restrictToUserId && (
          <AssigneeBadge
            label={t("assigneeFilter.unassigned")}
            count={byAssignee?.["unassigned"]}
            isSelected={selectedAssigneeId === "unassigned"}
            onClick={() => handleSelect("unassigned")}
            className="bg-gray-300 text-gray-600 hover:bg-gray-500"
          />
        )}
      </div>
    </div>
  );
};

type SelectableDraftMode = Extract<DraftMode, "only" | "onlyMine" | "overdue48h">;

type DraftModeOption = {
  mode: SelectableDraftMode;
  textClass: string;
  activeButtonClass: string;
  activeItemClass: string;
  badgeClass: string;
};

const DRAFT_MODE_OPTIONS: DraftModeOption[] = [
  {
    mode: "only",
    textClass: "text-gray-700",
    activeButtonClass: "border-black bg-gray-400 text-black",
    activeItemClass: "bg-gray-100",
    badgeClass: "bg-gray-800 text-white",
  },
  {
    mode: "onlyMine",
    textClass: "text-[#3562f8]",
    activeButtonClass: "border-[#3562f8] bg-[#3562f8]/10 text-[#3562f8]",
    activeItemClass: "bg-[#3562f8]/10",
    badgeClass: "bg-[#3562f8]/10 text-[#3562f8]",
  },
  {
    mode: "overdue48h",
    textClass: "text-red-600",
    activeButtonClass: "border-red-500 bg-red-100 text-red-600",
    activeItemClass: "bg-red-50",
    badgeClass: "bg-red-100 text-red-600",
  },
];

const DraftFilterButton = ({
  draftMode,
  counts,
  onSelect,
}: {
  draftMode: DraftMode;
  counts?: DraftCounts;
  onSelect: (mode: DraftMode) => void;
}) => {
  const { t } = useTranslation("boExpeditions");
  const [open, setOpen] = useState(false);
  const draftModeLabels = useMemo<Record<SelectableDraftMode, string>>(
    () => ({
      only: t("draftFilter.allDrafts"),
      onlyMine: t("draftFilter.myDrafts"),
      overdue48h: t("draftFilter.overdue48h"),
    }),
    [t],
  );
  const activeOption = DRAFT_MODE_OPTIONS.find((option) => option.mode === draftMode);

  const countFor = (mode: DraftMode) => {
    if (mode === "only") return counts?.drafts;
    if (mode === "onlyMine") return counts?.myDrafts;
    if (mode === "overdue48h") return counts?.lateDrafts;
    return undefined;
  };

  const triggerCount = activeOption ? countFor(draftMode) : counts?.drafts;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex flex-row items-center gap-2 rounded-md bg-gray-200 px-4 py-2 text-sm transition-all duration-150 hover:opacity-80",
            activeOption && cn("border", activeOption.activeButtonClass),
          )}
        >
          <Pencil className="size-3" />
          <span>{activeOption ? draftModeLabels[activeOption.mode] : t("draftFilter.title")}</span>
          {triggerCount !== undefined && (
            <span
              className={cn(
                "rounded-full px-1.5 py-0.5 text-xs font-medium",
                activeOption ? cn("bg-white/60", activeOption.textClass) : "bg-gray-800 text-white",
              )}
            >
              {triggerCount}
            </span>
          )}
          {activeOption && (
            <X
              className="h-4 w-4"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onSelect("all");
              }}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        <div className="flex flex-col">
          {DRAFT_MODE_OPTIONS.map((option) => {
            const isActive = draftMode === option.mode;
            const count = countFor(option.mode);

            return (
              <button
                key={option.mode}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSelect(isActive ? "all" : option.mode);
                }}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-100",
                  option.textClass,
                  isActive && cn("font-medium", option.activeItemClass),
                )}
              >
                <span>{draftModeLabels[option.mode]}</span>
                {count !== undefined && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-xs font-medium",
                      option.badgeClass,
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
